import React, { useEffect, useState } from "react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { UploadButton } from "@/components/Upload";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Save, Trash2, FileText, Cpu, Plus, Check, Power, Zap, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function ListEditor({ label, items, onChange, draft, onDraftChange, testid }) {
  const list = items || [];
  const add = () => {
    const v = (draft || "").trim();
    if (!v || list.includes(v)) { onDraftChange(""); return; }
    onChange([...list, v]);
    onDraftChange("");
  };
  const remove = (x) => onChange(list.filter((i) => i !== x));
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input value={draft || ""} onChange={(e) => onDraftChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={`Add a ${label.toLowerCase().replace(/s$/, "")}…`} data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {list.length === 0 && <span className="text-xs text-muted-foreground italic">None yet — add one above.</span>}
        {list.map((x) => (
          <span key={x} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-muted border border-border text-sm" data-testid={`${testid}-chip-${x}`}>
            {x}
            <button onClick={() => remove(x)} className="h-5 w-5 rounded-full inline-flex items-center justify-center hover:bg-destructive/15 hover:text-destructive transition-colors" data-testid={`${testid}-remove-${x}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function AIProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", provider: "openai-compatible", base_url: "", api_key: "", model: "" });
  const [testing, setTesting] = useState(null);

  const load = () => api.get("/ai-profiles").then((r) => setProfiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/ai-profiles", form);
      toast.success("AI profile added");
      setOpen(false); setForm({ name: "", provider: "openai-compatible", base_url: "", api_key: "", model: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const activate = async (id) => { await api.put(`/ai-profiles/${id}/activate`); toast.success("Active AI profile set"); load(); };
  const del = async (id) => { if (window.confirm("Delete this AI profile?")) { await api.delete(`/ai-profiles/${id}`); load(); } };
  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/ai-profiles/${id}/test`);
      data.ok ? toast.success("Connection OK ✓") : toast.error(`Test failed: ${data.detail || data.status}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(null); }
  };

  return (
    <SectionCard
      title="AI Profiles"
      action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-ai-btn"><Plus className="h-4 w-4 mr-1" /> Add Profile</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Add AI Profile</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Used to power AI suggestions in the Performance module. Works with any OpenAI-compatible endpoint (Kimi, DeepSeek, GLM, etc.).</p>
            <div className="space-y-3 py-2">
              <div><Label>Profile Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="e.g. Kimi K3" data-testid="ai-name" /></div>
              <div><Label>Base URL</Label><Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} className="mt-1.5" placeholder="https://api.provider.com/v1" data-testid="ai-baseurl" /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1.5" placeholder="e.g. kimi-k3 / deepseek-v4-pro / glm-5.7" data-testid="ai-model" /></div>
              <div><Label>API Key</Label><Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="mt-1.5" placeholder="sk-…" data-testid="ai-key" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.name || !form.base_url || !form.model || !form.api_key} data-testid="ai-save">Save Profile</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {profiles.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">
          <Cpu className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No AI profiles yet. Add one to enable AI drafting, summaries and recommendations in Performance.
        </div>
      )}
      <div className="space-y-2">
        {profiles.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`ai-profile-${p.id}`}>
            <div className={`h-9 w-9 rounded-md flex items-center justify-center ${p.active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}><Cpu className="h-4 w-4" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm flex items-center gap-2">{p.name} {p.active && <span className="px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px] border border-success/20">ACTIVE</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{p.model} · {p.api_key_masked}</p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => test(p.id)} disabled={testing === p.id} data-testid={`ai-test-${p.id}`} title="Test connection"><Zap className="h-4 w-4" /></Button>
            {!p.active && <Button size="icon" variant="ghost" className="h-8 w-8 text-success" onClick={() => activate(p.id)} data-testid={`ai-activate-${p.id}`} title="Set active"><Power className="h-4 w-4" /></Button>}
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(p.id)} data-testid={`ai-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

const PC_BLANK = { name: "", logo_file_id: null, stamp_file_id: null, address: "", uen: "", website: "", contact_email: "admin@shaabaz.com", payslip_note: "Confidential Payslip", payslip_footer: "This payslip is system-generated and confidential.", group_name: "", is_default: false };

function PayrollCompanies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(PC_BLANK);

  const load = () => api.get("/payroll-companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const edit = (c) => { setForm({ ...PC_BLANK, ...c }); setOpen(true); };
  const add = () => { setForm(PC_BLANK); setOpen(true); };

  const save = async () => {
    try {
      if (form.id) await api.put(`/payroll-companies/${form.id}`, form);
      else await api.post("/payroll-companies", form);
      toast.success("Payroll company saved");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Employees assigned to it must be reassigned first.`)) return;
    try { await api.delete(`/payroll-companies/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <SectionCard
      title="Payroll Companies"
      action={<Button size="sm" variant="outline" onClick={add} data-testid="add-payroll-company"><Plus className="h-4 w-4 mr-1" /> Add Company</Button>}
    >
      <p className="text-xs text-muted-foreground mb-4">Each employee is assigned a payroll company (on their Employment tab). Payslips show that company's name, logo, address, UEN and footer.</p>
      {companies.length === 0 && <div className="text-center py-6 text-sm text-muted-foreground"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No payroll companies yet.</div>}
      <div className="space-y-2">
        {companies.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`payroll-company-${c.id}`}>
            <div className="h-9 w-9 rounded-md flex items-center justify-center overflow-hidden bg-muted shrink-0">
              {c.logo_file_id ? <img src={fileUrl(c.logo_file_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm flex items-center gap-2 truncate">{c.name} {c.is_default && <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] border border-primary/20">DEFAULT</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{[c.uen ? `UEN ${c.uen}` : "", c.group_name].filter(Boolean).join(" · ") || c.address || "—"}</p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => edit(c)} data-testid={`edit-payroll-${c.id}`}><FileText className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(c)} data-testid={`del-payroll-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{form.id ? "Edit" : "Add"} Payroll Company</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0" data-testid="pc-logo-preview">
                {form.logo_file_id ? <img src={fileUrl(form.logo_file_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
              </div>
              <div className="flex gap-2">
                <UploadButton label={form.logo_file_id ? "Replace Logo" : "Upload Logo"} accept="image/*" onUploaded={(f) => setForm({ ...form, logo_file_id: f.id })} />
                {form.logo_file_id && <Button variant="outline" size="sm" className="text-destructive" onClick={() => setForm({ ...form, logo_file_id: null })} data-testid="pc-clear-logo"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0" data-testid="pc-stamp-preview">
                {form.stamp_file_id ? <img src={fileUrl(form.stamp_file_id)} alt="stamp" className="h-full w-full object-contain" /> : <span className="text-[10px] text-muted-foreground text-center px-1">Company Stamp</span>}
              </div>
              <div className="flex gap-2">
                <UploadButton label={form.stamp_file_id ? "Replace Stamp" : "Upload Stamp"} accept="image/*" onUploaded={(f) => setForm({ ...form, stamp_file_id: f.id })} />
                {form.stamp_file_id && <Button variant="outline" size="sm" className="text-destructive" onClick={() => setForm({ ...form, stamp_file_id: null })} data-testid="pc-clear-stamp"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
            <div><Label>Company Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="pc-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>UEN / Reg. No.</Label><Input value={form.uen} onChange={(e) => setForm({ ...form, uen: e.target.value })} className="mt-1.5" data-testid="pc-uen" /></div>
              <div><Label>Group Name</Label><Input value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })} className="mt-1.5" placeholder="e.g. Shaabaz Group" data-testid="pc-group" /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="pc-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Website</Label><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5" placeholder="www.company.com" data-testid="pc-website" /></div>
              <div><Label>Contact Email</Label><Input value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5" placeholder="admin@company.com" data-testid="pc-contact-email" /></div>
            </div>
            <div><Label>Payslip Header Tagline</Label><Input value={form.payslip_note} onChange={(e) => setForm({ ...form, payslip_note: e.target.value })} className="mt-1.5" data-testid="pc-note" /></div>
            <div><Label>Payslip Footer</Label><Textarea value={form.payslip_footer} onChange={(e) => setForm({ ...form, payslip_footer: e.target.value })} className="mt-1.5" data-testid="pc-footer" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="pc-default" /> Default payroll company <span className="text-xs text-muted-foreground">(used for employees with none assigned)</span></label>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.name} data-testid="pc-save">Save Company</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

function PublicHolidays() {
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ date: "", name: "" });
  const load = (y) => api.get(`/public-holidays?year=${y}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(year); }, [year]);
  const add = async () => {
    if (!form.date || !form.name.trim()) { toast.error("Enter a date and a name"); return; }
    try { await api.post("/public-holidays", form); toast.success("Holiday added"); setForm({ date: "", name: "" }); load(year); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/public-holidays/${id}`); load(year); } catch (e) { toast.error(apiErr(e)); } };
  const seed = async () => {
    try { const { data } = await api.post(`/public-holidays/seed?year=${year}`); toast.success(`Added ${data.added} SG holiday(s) for ${year}`); load(year); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const years = [year - 1, year, year + 1, year + 2].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <SectionCard title="Public Holidays" action={
      <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid="ph-year">
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    }>
      <p className="text-xs text-muted-foreground mb-3">Used to optionally exclude gazetted days from salary pro-rating (toggle in Payslip Customization). Seed the official Singapore list, or add your own.</p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-9 w-44" data-testid="ph-date" /></div>
        <div className="flex-1 min-w-[160px]"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-9" placeholder="e.g. National Day" data-testid="ph-name" /></div>
        <Button type="button" variant="outline" onClick={add} data-testid="ph-add"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        <Button type="button" onClick={seed} data-testid="ph-seed"><Zap className="h-4 w-4 mr-1" /> Seed SG {year}</Button>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No holidays for {year} yet.</p>}
        {items.map((h) => (
          <div key={h.id} className="flex items-center justify-between px-3 py-1.5 rounded-md border border-border text-sm" data-testid={`ph-row-${h.date}`}>
            <span><span className="font-mono-num text-muted-foreground mr-3">{h.date}</span>{h.name}</span>
            <button onClick={() => del(h.id)} className="text-muted-foreground hover:text-destructive" data-testid={`ph-del-${h.date}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}


export default function CompanySettings() {
  const [s, setS] = useState(null);
  const [divDraft, setDivDraft] = useState("");
  const [depDraft, setDepDraft] = useState("");

  const load = () => api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch ?? {
        company_name: s.company_name,
        address: s.address,
        payslip_note: s.payslip_note,
        payslip_footer: s.payslip_footer,
      });
      setS(data);
      localStorage.setItem("branding", JSON.stringify(data));
      window.dispatchEvent(new CustomEvent("branding-updated", { detail: data }));
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveLists = async () => {
    const dv = (s.divisions || []).slice();
    const dvv = divDraft.trim();
    if (dvv && !dv.includes(dvv)) dv.push(dvv);
    const dp = (s.departments || []).slice();
    const dpv = depDraft.trim();
    if (dpv && !dp.includes(dpv)) dp.push(dpv);
    setS({ ...s, divisions: dv, departments: dp });
    setDivDraft(""); setDepDraft("");
    await save({ divisions: dv, departments: dp });
  };

  if (!s) return null;

  return (
    <div className="animate-fade-up max-w-4xl">
      <PageTitle overline="Administration" title="Company Settings" />

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <SectionCard title="Payslip Customization">
            <div className="space-y-4">
              <div>
                <Label>Standard Daily Hours (for OT / off-day accrual)</Label>
                <Input type="number" step="0.5" value={s.standard_daily_hours ?? 8} onChange={(e) => setS({ ...s, standard_daily_hours: e.target.value })} className="mt-1.5 max-w-[160px]" data-testid="setting-daily-hours" />
                <p className="text-xs text-muted-foreground mt-1">Work beyond this per day accrues into the staff's Off-in-lieu (OIL) balance. 8 OIL hours = 1 off day.</p>
              </div>
              <div>
                <Label>Payslip Header Tagline</Label>
                <Input value={s.payslip_note || ""} onChange={(e) => setS({ ...s, payslip_note: e.target.value })} className="mt-1.5" placeholder="e.g. Confidential Payslip" data-testid="setting-note" />
              </div>
              <div>
                <Label>Payslip Footer</Label>
                <Textarea value={s.payslip_footer || ""} onChange={(e) => setS({ ...s, payslip_footer: e.target.value })} className="mt-1.5" placeholder="Text shown at the bottom of every payslip" data-testid="setting-footer" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={!!s.prorate_exclude_holidays} onChange={(e) => setS({ ...s, prorate_exclude_holidays: e.target.checked })} data-testid="setting-exclude-holidays" />
                <span><span className="text-sm font-medium">Exclude public holidays from salary pro-rating</span><span className="block text-xs text-muted-foreground">When on, gazetted holidays are not counted as working days when pro-rating a partial month. Manage the dates below.</span></span>
              </label>
            </div>
            <Button onClick={() => save({ payslip_note: s.payslip_note, payslip_footer: s.payslip_footer, standard_daily_hours: parseFloat(s.standard_daily_hours || 8), prorate_exclude_holidays: !!s.prorate_exclude_holidays })} className="mt-4" data-testid="save-settings"><Save className="h-4 w-4 mr-1.5" /> Save Changes</Button>
          </SectionCard>

          <SectionCard title="Attendance Verification">
            <label className="flex items-start gap-2 cursor-pointer mb-4 p-3 rounded-md border border-border bg-muted/30" data-testid="attendance-toggle-row">
              <input type="checkbox" className="mt-0.5" checked={s.attendance_enabled !== false} onChange={(e) => { setS({ ...s, attendance_enabled: e.target.checked }); save({ attendance_enabled: e.target.checked }); }} data-testid="setting-attendance-enabled" />
              <span><span className="text-sm font-medium">Enable attendance tracking</span><span className="block text-xs text-muted-foreground">When off, the Attendance menu and the dashboard Clock In/Out widgets are hidden for everyone and clocking in/out is blocked.</span></span>
            </label>
            <p className="text-xs text-muted-foreground mb-4">Verify where staff clock in/out from. Note: browsers can't read the WiFi name — we capture the device's public IP and (with permission) GPS location, then flag each punch as on/off the office network and on/off-site.</p>
            <div className="space-y-4">
              <div>
                <Label>Office public IP addresses</Label>
                <Textarea
                  value={(s.office_ips || []).join("\n")}
                  onChange={(e) => setS({ ...s, office_ips: e.target.value.split("\n") })}
                  className="mt-1.5 font-mono-num text-sm"
                  rows={3}
                  placeholder={"One per line, e.g.\n203.0.113.45\n118.201. (prefix match allowed)"}
                  data-testid="setting-office-ips"
                />
                <p className="text-xs text-muted-foreground mt-1">A punch from any of these IPs is marked “Office network”. Prefixes are allowed (e.g. <span className="font-mono-num">118.201.</span> matches the whole range). Tip: your office's current IP is <span className="font-mono-num">{s._detected_ip || "shown on any staff punch"}</span>.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Office latitude</Label><Input type="number" step="any" value={s.office_lat ?? ""} onChange={(e) => setS({ ...s, office_lat: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1.5" placeholder="1.3521" data-testid="setting-office-lat" /></div>
                <div><Label>Office longitude</Label><Input type="number" step="any" value={s.office_lng ?? ""} onChange={(e) => setS({ ...s, office_lng: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1.5" placeholder="103.8198" data-testid="setting-office-lng" /></div>
                <div><Label>Radius (m)</Label><Input type="number" value={s.office_radius_m ?? 200} onChange={(e) => setS({ ...s, office_radius_m: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1.5" data-testid="setting-office-radius" /></div>
              </div>
              <Button size="sm" variant="outline" onClick={() => {
                if (!navigator.geolocation) { toast.error("Geolocation not available in this browser"); return; }
                navigator.geolocation.getCurrentPosition(
                  (pos) => { setS((p) => ({ ...p, office_lat: pos.coords.latitude, office_lng: pos.coords.longitude })); toast.success("Filled with your current location"); },
                  () => toast.error("Could not get your location"),
                  { enableHighAccuracy: true, timeout: 8000 }
                );
              }} data-testid="use-my-location"><MapPin className="h-4 w-4 mr-1.5" /> Use my current location</Button>
              <p className="text-xs text-muted-foreground">Leave latitude/longitude blank to disable location (geofence) checks.</p>
            </div>
            <Button onClick={() => save({ office_ips: (s.office_ips || []).map((x) => x.trim()).filter(Boolean), office_lat: s.office_lat ?? null, office_lng: s.office_lng ?? null, office_radius_m: s.office_radius_m === "" || s.office_radius_m === undefined ? null : s.office_radius_m })} className="mt-4" data-testid="save-attendance-verify"><Save className="h-4 w-4 mr-1.5" /> Save Verification</Button>
          </SectionCard>

          <SectionCard title="Divisions & Departments">
            <p className="text-xs text-muted-foreground mb-4">Customize the lists used when adding or editing employees. Existing employees keep their current values.</p>
            <div className="space-y-5">
              <ListEditor label="Divisions" items={s.divisions} onChange={(v) => setS({ ...s, divisions: v })} draft={divDraft} onDraftChange={setDivDraft} testid="division" />
              <ListEditor label="Departments" items={s.departments} onChange={(v) => setS({ ...s, departments: v })} draft={depDraft} onDraftChange={setDepDraft} testid="department" />
            </div>
            <Button onClick={saveLists} className="mt-4" data-testid="save-org-lists"><Save className="h-4 w-4 mr-1.5" /> Save Lists</Button>
          </SectionCard>

          <PayrollCompanies />

          <PublicHolidays />

          <AIProfiles />
        </div>

        {/* Live preview */}
        <div className="lg:col-span-2">
          <SectionCard title="Payslip Preview">
            <div className="bg-white text-[#0A0A0A] p-4 rounded-md border border-border">
              <div className="flex items-start justify-between border-b-2 border-primary pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center overflow-hidden">
                    {s.logo_file_id ? <img src={fileUrl(s.logo_file_id)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-5 w-5 text-white" />}
                  </div>
                  <div>
                    <p className="font-heading font-extrabold text-base leading-none">{s.company_name || "Company"}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{s.address} · {s.payslip_note}</p>
                  </div>
                </div>
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Base Salary</span><span className="font-mono-num">$5,200.00</span></div>
                <div className="flex justify-between"><span>Commissions</span><span className="font-mono-num">$0.00</span></div>
                <div className="flex justify-between font-semibold text-foreground border-t border-border pt-1 mt-1"><span>Net Pay</span><span className="font-mono-num">$5,200.00</span></div>
              </div>
              <p className="mt-3 pt-2 border-t border-border text-[9px] text-muted-foreground">{s.payslip_footer}</p>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
